<template>
  <transition name="gamanza-tournament-slide">
    <aside v-if="tournament" class="gamanza-tournament-notification" role="status" aria-live="polite"
      :style="{ '--gamanza-primary': primaryColor }">
      <div class="gamanza-tournament-notification__glow"></div>
      <div class="gamanza-tournament-notification__icon">
        <i class="fas fa-trophy"></i>
      </div>
      <div class="gamanza-tournament-notification__content">
        <strong>{{ debug ? copy.debug : copy.active }}</strong>
        <span>{{ tournamentName }}</span>
        <small v-if="tournamentCount > 1">{{ copy.more(tournamentCount) }}</small>
      </div>
      <button type="button" class="gamanza-tournament-notification__open" @click="$emit('open')">
        {{ copy.open }}
        <i class="fas fa-arrow-right"></i>
      </button>
      <button type="button" class="gamanza-tournament-notification__close" :aria-label="copy.close"
        @click="$emit('dismiss')">
        <i class="fas fa-times"></i>
      </button>
    </aside>
  </transition>
</template>

<script>
export default {
  name: 'GamanzaTournamentNotification',
  props: {
    skin: {
      type: Object,
      required: true
    },
    tournament: {
      type: Object,
      default: null
    },
    tournamentCount: {
      type: Number,
      default: 1
    },
    debug: {
      type: Boolean,
      default: false
    },
    language: {
      type: String,
      default: 'es'
    }
  },
  computed: {
    primaryColor() {
      return this.skin?.gamanza?.lookAndFeel?.palette?.primary?.main || '#DA4167';
    },
    tournamentName() {
      const details = this.tournament?.externalDetails || [];
      const language = this.language.toLowerCase();
      const translation = details.find((detail) => detail.language === language) || details[0];

      return translation?.name || 'Torneo disponible';
    },
    copy() {
      const translations = {
        es: {
          active: 'Torneo activo',
          debug: 'Debug de torneos',
          open: 'Ver torneos',
          close: 'Cerrar',
          more: (count) => `Hay ${count} torneos activos.`
        },
        en: {
          active: 'Active tournament',
          debug: 'Tournament debug',
          open: 'View tournaments',
          close: 'Close',
          more: (count) => `${count} active tournaments are available.`
        },
        pt: {
          active: 'Torneio ativo',
          debug: 'Debug de torneios',
          open: 'Ver torneios',
          close: 'Fechar',
          more: (count) => `Ha ${count} torneios ativos.`
        }
      };

      return translations[this.language.toLowerCase()] || translations.es;
    }
  }
};
</script>

<style scoped>
.gamanza-tournament-notification {
  position: fixed;
  right: 24px;
  top: 84px;
  z-index: 10002;
  display: flex;
  align-items: center;
  gap: 16px;
  isolation: isolate;
  max-width: min(540px, calc(100vw - 32px));
  min-height: 100px;
  padding: 18px 18px 18px 22px;
  color: #fff;
  background: linear-gradient(115deg, #111220 0%, #18172b 58%, #211a30 100%);
  border: 1px solid var(--gamanza-primary);
  border-radius: 14px;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.42), 0 0 28px rgba(218, 65, 103, 0.18);
  overflow: hidden;
}

.gamanza-tournament-notification::before {
  position: absolute;
  z-index: 0;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  content: '';
  background: var(--gamanza-primary);
}

.gamanza-tournament-notification__glow {
  position: absolute;
  z-index: 0;
  top: -48px;
  right: -28px;
  width: 132px;
  height: 132px;
  background: var(--gamanza-primary);
  border-radius: 50%;
  filter: blur(44px);
  opacity: 0.26;
}

.gamanza-tournament-notification > :not(.gamanza-tournament-notification__glow) {
  position: relative;
  z-index: 1;
}

.gamanza-tournament-notification__icon {
  display: grid;
  position: relative;
  isolation: isolate;
  flex: 0 0 62px;
  width: 62px;
  height: 62px;
  color: #151526;
  background: radial-gradient(circle at 35% 28%, #fff 0 7%, var(--gamanza-primary) 34%, var(--gamanza-primary) 100%);
  border-radius: 50%;
  box-shadow: 0 0 0 7px rgba(255, 255, 255, 0.07), 0 10px 22px rgba(0, 0, 0, 0.28);
  place-items: center;
}

.gamanza-tournament-notification__icon::before,
.gamanza-tournament-notification__icon::after {
  position: absolute;
  z-index: 0;
  inset: -8px;
  content: '';
  border: 1px solid var(--gamanza-primary);
  border-radius: 50%;
  opacity: 0;
  animation: gamanza-trophy-ring 2.6s ease-out infinite;
}

.gamanza-tournament-notification__icon::after {
  animation-delay: 1.3s;
}

.gamanza-tournament-notification__icon i {
  position: relative;
  z-index: 1;
  font-size: 1.6rem;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.38);
  animation: gamanza-trophy-float 2.6s ease-in-out infinite;
}

.gamanza-tournament-notification__content {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.gamanza-tournament-notification__content strong {
  font-size: 1.02rem;
  letter-spacing: 0.01em;
}

.gamanza-tournament-notification__content span {
  margin-top: 3px;
  font-size: 0.96rem;
}

.gamanza-tournament-notification__content span,
.gamanza-tournament-notification__content small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gamanza-tournament-notification__content small {
  margin-top: 2px;
  color: #d1ceda;
  font-size: 0.75rem;
}

.gamanza-tournament-notification__open,
.gamanza-tournament-notification__close {
  border: 0;
  cursor: pointer;
}

.gamanza-tournament-notification__open {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 11px 13px;
  color: #151526;
  background: var(--gamanza-primary);
  border-radius: 7px;
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.2);
  font-weight: 700;
  transition: transform 180ms ease, box-shadow 180ms ease;
  white-space: nowrap;
}

.gamanza-tournament-notification__open:hover {
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.3);
  transform: translateX(-2px);
}

.gamanza-tournament-notification__close {
  padding: 4px;
  color: #fff;
  background: transparent;
}

.gamanza-tournament-slide-enter-active {
  animation: gamanza-tournament-slide-in 560ms cubic-bezier(0.16, 1, 0.3, 1);
}

.gamanza-tournament-slide-leave-active {
  animation: gamanza-tournament-slide-out 220ms ease-in forwards;
}

@keyframes gamanza-tournament-slide-in {
  from {
    opacity: 0;
    transform: translateX(calc(100% + 32px));
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes gamanza-tournament-slide-out {
  to {
    opacity: 0;
    transform: translateX(calc(100% + 32px));
  }
}

@keyframes gamanza-trophy-ring {
  0% {
    opacity: 0.72;
    transform: scale(0.7);
  }

  72%,
  100% {
    opacity: 0;
    transform: scale(1.34);
  }
}

@keyframes gamanza-trophy-float {
  0%,
  100% {
    transform: translateY(0) rotate(-3deg);
  }

  50% {
    transform: translateY(-3px) rotate(3deg);
  }
}

@media (max-width: 768px) {
  .gamanza-tournament-notification {
    top: 12px;
    right: 12px;
    align-items: flex-start;
    gap: 12px;
    min-height: 86px;
    padding: 14px;
  }

  .gamanza-tournament-notification__icon {
    flex-basis: 48px;
    width: 48px;
    height: 48px;
  }

  .gamanza-tournament-notification__icon i {
    font-size: 1.25rem;
  }

  .gamanza-tournament-notification__open {
    margin-top: 2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .gamanza-tournament-slide-enter-active,
  .gamanza-tournament-slide-leave-active,
  .gamanza-tournament-notification__icon::before,
  .gamanza-tournament-notification__icon::after,
  .gamanza-tournament-notification__icon i {
    animation: none;
  }
}
</style>
